import { useState, type KeyboardEvent } from "react";
import type { UserLookup } from "@/hooks/use-users";
import { isListNavKey, moveHighlight } from "@/lib/list-navigation";

const MAX_VISIBLE = 8;

export interface MentionComposerOptions {
  users: UserLookup[];
  /** Prefiks for listbox-/option-id-er, så flere komposere på samme side ikke kolliderer. */
  idPrefix?: string;
}

/**
 * Felles @-tagging for kommentarfelt: hovedfeltet i Prosjektoppdateringer og
 * svarfeltet under en kommentar bruker samme regler (VIB-2541). Hooken eier
 * teksten og hvem som er tagget; komponenten eier rendering og sending.
 *
 * `handleKeyDown` returnerer true når tastetrykket ble brukt av @-listen, så
 * kalleren ikke sender meldingen på samme Enter.
 */
export function useMentionComposer({ users, idPrefix = "mention" }: MentionComposerOptions) {
  const [body, setBody] = useState("");
  const [mentionedIds, setMentionedIds] = useState<string[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [highlighted, setHighlighted] = useState(0);

  const visibleUsers = users
    .filter((u) => u.full_name.toLowerCase().includes(mentionFilter))
    .slice(0, MAX_VISIBLE);
  const mentionsOpen = showMentions && visibleUsers.length > 0;
  const highlightedUser = mentionsOpen
    ? visibleUsers[Math.min(highlighted, visibleUsers.length - 1)]
    : undefined;

  const insertMention = (user: UserLookup) => {
    const beforeAt = body.lastIndexOf("@");
    const before = beforeAt >= 0 ? body.slice(0, beforeAt) : body;
    setBody(`${before}@${user.full_name} `);
    setMentionedIds((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]));
    setShowMentions(false);
  };

  const handleInput = (val: string) => {
    setBody(val);
    const lastAt = val.lastIndexOf("@");
    if (lastAt >= 0 && !val.slice(lastAt).includes(" ")) {
      setShowMentions(true);
      setMentionFilter(val.slice(lastAt + 1).toLowerCase());
      setHighlighted(0);
    } else {
      setShowMentions(false);
    }
  };

  /** Piler flytter uthevingen, Enter/Tab velger, Escape lukker. */
  const handleKeyDown = (e: KeyboardEvent): boolean => {
    if (mentionsOpen) {
      if (isListNavKey(e.key)) {
        e.preventDefault();
        setHighlighted((h) => moveHighlight(h, e.key as "ArrowDown", visibleUsers.length));
        return true;
      }
      if ((e.key === "Enter" || e.key === "Tab") && highlightedUser) {
        e.preventDefault();
        insertMention(highlightedUser);
        return true;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowMentions(false);
        return true;
      }
    }
    if (e.key === "@") {
      setShowMentions(true);
      setMentionFilter("");
      setHighlighted(0);
    }
    return false;
  };

  /** Tagget-id-er der navnet fortsatt står i teksten (bruker kan ha slettet @-navnet). */
  const resolveMentionedIds = (): string[] =>
    mentionedIds.filter((id) => {
      const u = users.find((x) => x.id === id);
      return u ? body.includes(`@${u.full_name}`) : false;
    });

  const reset = () => {
    setBody("");
    setMentionedIds([]);
    setShowMentions(false);
    setMentionFilter("");
    setHighlighted(0);
  };

  return {
    body,
    setBody: handleInput,
    mentionsOpen,
    visibleUsers,
    highlightedUser,
    setHighlighted,
    insertMention,
    handleKeyDown,
    resolveMentionedIds,
    reset,
    listboxId: `${idPrefix}-listbox`,
    optionId: (userId: string) => `${idPrefix}-opt-${userId}`,
  };
}

export type MentionComposer = ReturnType<typeof useMentionComposer>;
